import { CartLinesUpdateEvent, StandardEvents } from "@shopify/events";

// Shopping helpers: a size must be chosen before adding to the bag, card colour swatches, the quick-add sheet and a short product intro.
const html = document.documentElement;
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
const phone = matchMedia("(max-width: 749px)");
const rootUrl = window.Shopify?.routes?.root || "/";
const scrollBehavior = () => (reducedMotion.matches ? "instant" : "smooth");

/* Size gate: the product page starts with no size chosen, on every screen size. */
function sizeFieldset() {
  return [...document.querySelectorAll(".product-information .variant-option")].find((fieldset) =>
    /^size/i.test(fieldset.querySelector("legend")?.textContent.trim() || ""),
  );
}

// A ?variant= in the address means a size was chosen (by this shopper, a shared link or a bag line). Card and
// colour links carry ?option_values= (colour only), so they never imply a size.
if (sizeFieldset() && !new URL(window.location.href).searchParams.has("variant")) html.classList.add("sp-size-unset");

// Round 4A: while no size is chosen the product page's Add button and the sticky bar's show "Select a size" through
// CSS (their own text is hidden), so that is also their accessible name (what is seen is what is heard, WCAG 2.5.3);
// the name goes once a size is chosen.
function syncAddLabels() {
  const unset = html.classList.contains("sp-size-unset");
  document
    .querySelectorAll('.product-information button[name="add"], sticky-add-to-cart .sticky-add-to-cart__button')
    .forEach((button) => {
      if (unset) button.setAttribute("aria-label", "Select a size");
      else button.removeAttribute("aria-label");
    });
}
syncAddLabels();

function promptForSize() {
  const fieldset = sizeFieldset();
  if (!fieldset) return;
  if (!fieldset.querySelector(".sp-size-error")) {
    const message = document.createElement("p");
    message.className = "sp-size-error";
    message.setAttribute("role", "alert");
    message.textContent = "Please choose a size.";
    fieldset.append(message);
  }
  fieldset.scrollIntoView({ block: "center", behavior: scrollBehavior() });
  fieldset.querySelector("input")?.focus({ preventScroll: true });
}

document.addEventListener(
  "click",
  (event) => {
    if (!(event.target instanceof Element)) return;
    // Any tap on a size, including the one Shopify pre-checks, counts as a choice.
    const sizeInput = event.target.closest(".product-information input[data-option-name]");
    if (sizeInput instanceof HTMLInputElement && /^size$/i.test(sizeInput.dataset.optionName || "")) {
      html.classList.remove("sp-size-unset");
      syncAddLabels();
      document.querySelector(".sp-size-error")?.remove();
      scheduleUrlSync(); // the pre-checked size fires no change, so the address would not get ?variant= otherwise
      // Nor does it redraw the picker: show the low-stock line for it now (after the tap has checked the size).
      setTimeout(markSizesForColour, 0);
      return;
    }
    if (!html.classList.contains("sp-size-unset")) return;
    const purchase = event.target.closest(
      '.product-information button[name="add"], .product-information .shopify-payment-button, sticky-add-to-cart .sticky-add-to-cart__button',
    );
    if (!purchase) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    promptForSize();
  },
  true,
);

document.addEventListener(
  "submit",
  (event) => {
    if (!html.classList.contains("sp-size-unset")) return;
    if (!(event.target instanceof Element) || !event.target.closest(".product-information")) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    promptForSize();
  },
  true,
);

/* Card colours (snippets/sp-card.liquid). A swatch puts its colour's front photo on the card and points the card's
   links and quick add at that colour, so the product page opens in it.
   - Mouse: resting on a swatch previews that colour and keeps it; a click opens the product in it.
   - Keyboard: focusing a swatch previews it; Enter opens it.
   - Touch and pen: a tap selects the colour in place, so the photo can be checked before opening it.
   The collection filters ask for a colour with the sp:card-colour event (see sp-collection.js). */
const cardWidths = [320, 540, 900]; // the widths sp-card.liquid's image_tag offers
const resized = (url, width) => url.replace(/([?&]width=)\d+/, `$1${width}`);
const srcsetFor = (url) => cardWidths.map((width) => `${resized(url, width)} ${width}w`).join(", ");
const dotSelector = ".sp-card-dots a[data-sp-colour]";
const photoRequests = new WeakMap(); // card -> the front photo it was last asked to show

// The ring is rendered by sp-card.liquid and positioned in CSS from --sp-ring-index (dots are 44px wide), so moving
// it never measures the page. (Measuring every card at load used to cost ~2 s of main thread on a slow phone.)
function placeRing(nav, dot) {
  nav.style.setProperty("--sp-ring-index", String([...nav.querySelectorAll("a[data-sp-colour]")].indexOf(dot)));
  if (nav.querySelector(".sp-dot-ring")) return;
  const ring = document.createElement("span");
  ring.className = "sp-dot-ring";
  ring.setAttribute("aria-hidden", "true");
  nav.prepend(ring);
  nav.classList.add("has-ring");
}

// The new photo is fully loaded and decoded first, then replaces the old one in a single step (Round 11B fix: no
// crossfade, so two photos are never seen through each other), so the card never flashes empty or half drawn. The
// second photo (hover) is kept out of sight until its new colour's picture has loaded. A shopper moving quickly
// across swatches only ever ends on the last colour asked for. Round 4A: a photo that fails to load is never
// swapped in (the old one stays), each photo keeps its own crop position (focal point, or near the top for a
// portrait; data-sp-*-pos from sp-card.liquid), and a colour with no photo of its own leaves the photo as it is.
function swapCardPhoto(card, front, back, alt, frontPos = "", backPos = "") {
  const second = card.querySelector(".sp-card-photo .sp-card-alt");
  if (second) {
    // The second photo (desktop hover) follows the colour, or steps aside when that colour has none.
    second.hidden = !back;
    if (back) {
      second.style.objectPosition = backPos;
      holdUntilLoaded(second);
      second.srcset = srcsetFor(back);
      second.src = resized(back, 540);
    }
  }
  const main = card.querySelector(".sp-card-photo .sp-card-main");
  if (!main || !front || photoRequests.get(card) === front) return;
  photoRequests.set(card, front);
  // Loaded off the page with the card's own sizes, so the same file is chosen and is ready when it is swapped in.
  const next = new Image();
  next.sizes = main.sizes;
  next.srcset = srcsetFor(front);
  next.src = resized(front, 540);
  next
    .decode()
    .then(
      () => true,
      () => false,
    )
    .then((decoded) => {
      if (photoRequests.get(card) !== front) return;
      if (!decoded) {
        // The new colour's photo could not be loaded: keep the one on show.
        photoRequests.delete(card);
        return;
      }
      main.style.objectPosition = frontPos;
      main.srcset = next.srcset;
      main.src = next.src;
      main.alt = alt;
    });
}

// A photo that is about to change stays out of sight (sp-img-loading: opacity 0) until its new picture has loaded, so
// a half-drawn or previous colour's photo never shows.
function holdUntilLoaded(image) {
  image.classList.add("sp-img-loading");
  const done = () => image.classList.remove("sp-img-loading");
  image.addEventListener("load", done, { once: true });
  image.addEventListener("error", done, { once: true });
}

function selectCardColour(card, dot, { announce = false } = {}) {
  if (!card || !dot || dot.getAttribute("aria-current") === "true") return;
  const nav = dot.closest(".sp-card-dots");
  nav.querySelectorAll("a[aria-current]").forEach((other) => other.removeAttribute("aria-current"));
  dot.setAttribute("aria-current", "true");
  placeRing(nav, dot);
  const colour = dot.dataset.spColour;
  card.dataset.spColour = colour;
  swapCardPhoto(
    card,
    dot.dataset.spFront,
    dot.dataset.spBack,
    dot.getAttribute("aria-label") || "",
    dot.dataset.spFrontPos || "",
    dot.dataset.spBackPos || "",
  );
  card
    .querySelectorAll("a.sp-card-photo, .sp-card-copy h3 a")
    .forEach((link) => link.setAttribute("href", dot.getAttribute("href")));
  const label = card.querySelector("[data-sp-colour-label]");
  if (label) label.textContent = colour;
  renderCardSizes(card); // the hover size row follows the colour on show
  if (announce) announceStatus(`${colour} selected`);
}

// Fetch a card's other colour photos as soon as a mouse enters it, so the first swatch hover is instant. Touch
// screens skip this: it would download every colour of every card scrolled past.
function warmCard(card) {
  if (!card || card.dataset.spWarm) return;
  card.dataset.spWarm = "true";
  const sizes = card.querySelector(".sp-card-photo .sp-card-main")?.sizes || "50vw";
  card.querySelectorAll(`${dotSelector}:not([aria-current])`).forEach((dot) => {
    if (!dot.dataset.spFront) return;
    const image = new Image();
    image.sizes = sizes;
    image.srcset = srcsetFor(dot.dataset.spFront);
  });
}

let lastPointer = { type: "", time: 0 };
document.addEventListener(
  "pointerdown",
  (event) => (lastPointer = { type: event.pointerType, time: event.timeStamp }),
  { capture: true, passive: true },
);

let hoverTimer;
document.addEventListener(
  "pointerover",
  (event) => {
    if (event.pointerType !== "mouse" || !(event.target instanceof Element)) return;
    warmCard(event.target.closest("[data-sp-card]"));
    clearTimeout(hoverTimer);
    // A short pause, so sweeping across a card on the way to something else changes nothing.
    const dot = event.target.closest(dotSelector);
    if (dot) hoverTimer = setTimeout(() => selectCardColour(dot.closest("[data-sp-card]"), dot), 60);
  },
  { passive: true },
);

document.addEventListener("focusin", (event) => {
  const dot = event.target instanceof Element ? event.target.closest(dotSelector) : null;
  if (dot?.matches(":focus-visible")) selectCardColour(dot.closest("[data-sp-card]"), dot);
});

// Registered before the card transition handler below, which skips clicks this one has handled.
document.addEventListener("click", (event) => {
  const dot = event.target instanceof Element ? event.target.closest(dotSelector) : null;
  if (!dot || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const card = dot.closest("[data-sp-card]");
  const tapped = ["touch", "pen"].includes(lastPointer.type) && event.timeStamp - lastPointer.time < 1000;
  if (!tapped) {
    // Mouse, keyboard and assistive technology: the link opens the product in this colour.
    selectCardColour(card, dot);
    return;
  }
  event.preventDefault();
  selectCardColour(card, dot, { announce: true });
});

function applyColourWish(card, colour) {
  delete card.dataset.spWantColour;
  const dot = [...card.querySelectorAll(dotSelector)].find((candidate) => candidate.dataset.spColour === colour);
  if (dot) selectCardColour(card, dot);
}
document.addEventListener("sp:card-colour", (event) => {
  const card = event.target instanceof Element ? event.target.closest("[data-sp-card]") : null;
  if (card) applyColourWish(card, event.detail?.colour);
});
// Wishes made by sp-collection.js before this script was running (it loads async and can start first).
document
  .querySelectorAll("[data-sp-card][data-sp-want-colour]")
  .forEach((card) => applyColourWish(card, card.dataset.spWantColour));

/* Bag line photos that fail to load (Round 25 fix): first the product's other photo (data-sp-fallback on the frame,
   snippets/cart-products.liquid), shown as the frame's background once it has loaded (.sp-thumb-other); then the
   product's name on the frame's grey (.sp-thumb-failed); never an unexplained blank (sp-commerce.css). The failed
   image element is left alone (only made invisible, so its alt text still names the link): Safari keeps an image
   element that once failed in its broken state and never paints a new photo in it. Error events don't bubble, so
   this listens in the capture phase; it also covers the drawer's lines that Horizon redraws after a change. A photo
   that failed before this script ran is caught once here. */
function bagPhotoFailed(image) {
  const frame = image.closest(".cart-items__media-container");
  if (!frame || frame.matches(".sp-thumb-other, .sp-thumb-failed, [data-sp-trying]")) return;
  const other = frame.dataset.spFallback;
  if (!other) {
    frame.classList.add("sp-thumb-failed");
    return;
  }
  frame.setAttribute("data-sp-trying", "");
  const probe = new Image();
  probe.onload = () => {
    frame.style.backgroundImage = `url("${probe.src}")`;
    frame.classList.add("sp-thumb-other");
    frame.removeAttribute("data-sp-trying");
  };
  probe.onerror = () => {
    frame.classList.add("sp-thumb-failed");
    frame.removeAttribute("data-sp-trying");
  };
  probe.src = other;
}
document.addEventListener(
  "error",
  (event) => {
    if (event.target instanceof HTMLImageElement && event.target.matches(".cart-items__media-image")) bagPhotoFailed(event.target);
  },
  true,
);
document
  .querySelectorAll(".cart-items__media-image")
  .forEach((image) => image.complete && image.naturalWidth === 0 && image.getAttribute("src") && bagPhotoFailed(image));

/* Quick add: the bag button on a card opens a sheet with the card's colours and sizes; a tap on a size adds it.
   Sizes and stock come straight from the card (data-sp-variants), so the sheet is complete the moment it opens.
   Stock is then refreshed from Shopify (/products/<handle>.js, usually already fetched by the time the finger or
   pointer reached the button). Shopify's own add takes about 0.65–0.9 s on this store, so the size fills and
   "Adding…" shows in the same frame as the tap. One add runs at a time: a double tap cannot add twice. */
const products = new Map();
let sheet;
let opener;
let current; // { card, handle, title, variants, colour }
let adding = false;

const sheetPart = (name) => sheet.querySelector(`[data-sp-sheet="${name}"]`);

// The close cross repeats snippets/sp-icon.liquid (name: 'close'): this sheet is built here, not in Liquid.
function buildSheet() {
  sheet = document.createElement("dialog");
  sheet.className = "sp-sheet sp-quick-add";
  sheet.setAttribute("aria-labelledby", "sp-sheet-title");
  sheet.innerHTML = `<div class="sp-sheet-head"><img class="sp-sheet-photo" data-sp-sheet="photo" alt="" width="64" height="85"><div class="sp-sheet-heading"><p class="sp-sheet-title" id="sp-sheet-title" data-sp-sheet="title"></p><p class="sp-sheet-meta" data-sp-sheet="meta"></p></div><button type="button" class="sp-sheet-close" data-sp-sheet-close aria-label="Close"><svg class="sp-icon sp-icon-close" viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke"/></svg></button></div>
<div class="sp-sheet-colours" role="group" aria-labelledby="sp-sheet-colour-label" data-sp-sheet="colours" hidden><p class="sp-sheet-label" id="sp-sheet-colour-label">Colour <span data-sp-sheet="colour"></span></p><div class="sp-sheet-colour-list" data-sp-sheet="colour-list"></div></div>
<div class="sp-sheet-size-head"><p class="sp-sheet-label" id="sp-sheet-size-label">Select a size</p><a class="sp-sheet-guide" href="${rootUrl}pages/terms-and-guidelines#size-guide">Size guide</a></div>
<div class="sp-sheet-sizes" role="group" aria-labelledby="sp-sheet-size-label" data-sp-sheet="sizes"></div>
<p class="sp-sheet-status" role="status" data-sp-sheet="status"></p><a class="sp-sheet-details" data-sp-sheet="details" href="">View full details</a>`;
  document.body.append(sheet);
  sheet.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;
    const rect = sheet.getBoundingClientRect();
    const outside =
      event.target === sheet &&
      (event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom);
    if (outside || event.target.closest("[data-sp-sheet-close]")) return sheet.close();
    const colour = event.target.closest("[data-sp-sheet-colour]");
    if (colour && !adding) return chooseSheetColour(colour.dataset.spSheetColour);
    const size = event.target.closest("[data-variant-id]");
    if (!size) return;
    if (adding) {
      if (!size.classList.contains("is-adding"))
        sheetPart("status").textContent = "One moment, still adding to your bag…";
      return;
    }
    if (size.classList.contains("is-sold-out")) {
      sheetPart("status").textContent =
        `${size.dataset.size} is sold out${current.colour ? ` in ${current.colour}` : ""}.`;
      return;
    }
    addToBag(size);
  });
  sheet.addEventListener("close", () => opener?.focus({ preventScroll: true }));
}

const optionName = (option) => (typeof option === "string" ? option : option.name);

/* Product data is fetched once per product, early: on first touch, hover or focus of a quick-add button. */
function loadProduct(handle) {
  if (!products.has(handle)) {
    const request = fetch(`${rootUrl}products/${handle}.js`).then((response) => {
      if (!response.ok) throw new Error(`Product ${handle} unavailable`);
      return response.json();
    });
    request.catch(() => products.delete(handle));
    products.set(handle, request);
  }
  return products.get(handle);
}
// A page restored by Back may be hours old: ask Shopify for stock again.
window.addEventListener("pageshow", (event) => {
  if (event.persisted) products.clear();
});

const prefetchQuickAdd = (event) => {
  const button = event.target instanceof Element ? event.target.closest("[data-sp-qa]") : null;
  if (button) loadProduct(button.dataset.spQa).catch(() => {});
};
["touchstart", "pointerdown", "pointerover", "focusin"].forEach((type) =>
  document.addEventListener(type, prefetchQuickAdd, { passive: true, capture: true }),
);

// Same shape as the card's data-sp-variants, from Shopify's product JSON (used when a card has no data of its own).
function variantsFromProduct(product) {
  const sizeIndex = product.options.findIndex((option) => /size/i.test(optionName(option)));
  const colourIndex = product.options.findIndex((option) => /colou?r/i.test(optionName(option)));
  return product.variants.map((variant) => ({
    id: variant.id,
    colour: colourIndex < 0 ? "" : variant.options[colourIndex],
    size: sizeIndex < 0 ? "" : variant.options[sizeIndex], // no size option: the sheet says "One size"
    available: variant.available,
    price: variant.price,
  }));
}

function variantsFromCard(card) {
  try {
    return JSON.parse(card?.dataset.spVariants || "[]").map(([id, colour, size, available, price]) => ({
      id,
      colour,
      size,
      available: available === 1,
      price,
    }));
  } catch {
    return [];
  }
}

function setSizeStock(button, available) {
  button.classList.toggle("is-sold-out", !available);
  if (available) button.removeAttribute("aria-disabled");
  else button.setAttribute("aria-disabled", "true");
  button.querySelector(".sp-visually-hidden")?.remove();
  if (!available) button.insertAdjacentHTML("beforeend", '<span class="sp-visually-hidden">, sold out</span>');
}

function sheetVariants() {
  return current.variants.filter((variant) => !current.colour || variant.colour === current.colour);
}

function stockNote() {
  const variants = sheetVariants();
  if (!variants.length || variants.some((variant) => variant.available)) return "";
  const other = current.variants.find((variant) => variant.available && variant.colour !== current.colour);
  return `${current.colour || "This style"} is sold out in every size.${other?.colour ? ` ${other.colour} is available.` : ""}`;
}

function renderSizes() {
  const sizes = sheetPart("sizes");
  sizes.removeAttribute("aria-busy");
  sizes.replaceChildren(
    ...sheetVariants().map((variant) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.variantId = String(variant.id);
      button.dataset.size = variant.size || "One size";
      const label = document.createElement("span");
      label.textContent = button.dataset.size;
      button.append(label);
      setSizeStock(button, variant.available);
      return button;
    }),
  );
  sheetPart("status").textContent = stockNote();
}

function renderColours() {
  const colours = [...new Set(current.variants.map((variant) => variant.colour).filter(Boolean))];
  const group = sheetPart("colours");
  // Round 23: one colour is shown too (its one swatch, chosen), as on the product page: every style has the same rows.
  group.hidden = colours.length < 1;
  sheetPart("colour").textContent = current.colour;
  if (colours.length < 1) return;
  let swatches = {};
  try {
    swatches = JSON.parse(current.card?.dataset.spSwatches || "{}");
  } catch {
    swatches = {};
  }
  sheetPart("colour-list").replaceChildren(
    ...colours.map((colour) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.spSheetColour = colour;
      button.setAttribute("aria-pressed", String(colour === current.colour));
      // The card's swatch for every colour (sp-card.liquid, data-sp-swatches); none set up yet: the dashed ring.
      const swatch = document.createElement("span");
      swatch.className = "sp-sheet-swatch";
      swatch.setAttribute("aria-hidden", "true");
      if (swatches[colour]) swatch.style.background = swatches[colour];
      else swatch.classList.add("sp-swatch-unset");
      button.append(swatch);
      button.append(colour);
      return button;
    }),
  );
}

// On opening, the sheet shows the card's own photo (already loaded, so it appears at once). After a colour change it
// shows that colour's photo, and the previous one is hidden until it arrives: the sheet's image element is reused,
// and browsers keep showing the old picture (even another product's) while a new one downloads.
function sheetPhoto({ fromCard = false } = {}) {
  const dot = [...(current.card?.querySelectorAll(dotSelector) || [])].find(
    (candidate) => candidate.dataset.spColour === current.colour,
  );
  const main = current.card?.querySelector(".sp-card-photo .sp-card-main");
  const photo = sheetPart("photo");
  const cardSrc = main?.currentSrc || main?.src || "";
  const src = (fromCard && cardSrc) || (dot?.dataset.spFront ? resized(dot.dataset.spFront, 180) : cardSrc);
  photo.hidden = !src;
  if (!src || photo.src === new URL(src, window.location.href).href) return;
  photo.style.visibility = "hidden";
  photo.onload = photo.onerror = () => (photo.style.visibility = "");
  photo.src = src;
  if (photo.complete) photo.style.visibility = "";
}

function chooseSheetColour(colour) {
  if (colour === current.colour) return;
  current.colour = colour;
  // The card follows the sheet, so closing the sheet leaves the shopper looking at the colour they chose.
  current.card?.dispatchEvent(new CustomEvent("sp:card-colour", { bubbles: true, detail: { colour } }));
  sheetPart("colour-list")
    .querySelectorAll("[data-sp-sheet-colour]")
    .forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.spSheetColour === colour)));
  sheetPart("colour").textContent = colour;
  sheetPart("details").href =
    current.card?.querySelector("a.sp-card-photo")?.getAttribute("href") || sheetPart("details").href;
  sheetPhoto();
  renderSizes();
}

function openSheet(button) {
  if (!sheet) buildSheet();
  opener = button;
  const card = button.closest("[data-sp-card]");
  const handle = button.dataset.spQa;
  current = { card, handle, variants: variantsFromCard(card), colour: card?.dataset.spColour || "" };
  sheetPart("title").textContent = card?.querySelector("h3")?.textContent.trim() || "";
  sheetPart("meta").textContent = card?.querySelector(".sp-card-copy strong")?.textContent.trim() || "";
  sheetPart("details").href =
    card?.querySelector("a.sp-card-photo")?.getAttribute("href") || `${rootUrl}products/${handle}`;
  sheetPart("status").textContent = "";
  sheetPhoto({ fromCard: true });
  if (current.variants.length) {
    renderColours();
    renderSizes();
  } else {
    // No card data: placeholders until Shopify's product JSON arrives.
    sheetPart("colours").hidden = true;
    sheetPart("sizes").setAttribute("aria-busy", "true");
    sheetPart("sizes").replaceChildren(
      ...Array.from({ length: 5 }, () => {
        const placeholder = document.createElement("span");
        placeholder.dataset.spPlaceholder = "";
        placeholder.setAttribute("aria-hidden", "true");
        return placeholder;
      }),
    );
  }
  if (!sheet.open) sheet.showModal();
  refreshStock(current);
}

// Shopify's current stock replaces the card's (the page may have been open a while). Buttons are updated in place, so
// a size the shopper is about to tap does not move or lose focus.
async function refreshStock(view) {
  let product;
  try {
    product = await loadProduct(view.handle);
  } catch {
    if (current === view && !view.variants.length) {
      sheetPart("sizes").removeAttribute("aria-busy");
      sheetPart("status").textContent = "Sizes could not load. Please try again.";
    }
    return;
  }
  const fresh = variantsFromProduct(product);
  if (current !== view || !sheet.open) return;
  if (!view.variants.length) {
    view.variants = fresh;
    if (!view.colour) view.colour = fresh.find((variant) => variant.available)?.colour || fresh[0]?.colour || "";
    sheetPart("title").textContent = product.title;
    renderColours();
    renderSizes();
    return;
  }
  const stock = new Map(fresh.map((variant) => [String(variant.id), variant.available]));
  view.variants.forEach((variant) => {
    if (stock.has(String(variant.id))) variant.available = stock.get(String(variant.id));
  });
  if (adding) return;
  sheetPart("sizes")
    .querySelectorAll("[data-variant-id]")
    .forEach((button) => {
      if (stock.has(button.dataset.variantId)) setSizeStock(button, stock.get(button.dataset.variantId));
    });
  sheetPart("status").textContent = stockNote();
}

// Same as product-form.js: the cart from the add response, with the token from the cart cookie and applied codes.
function cartFromSections(sections) {
  const html = sections?.["sp-cart-state"];
  if (!html) return null;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const json = doc.querySelector("[data-sp-cart-state]")?.textContent;
    if (!json) return null;
    const cart = JSON.parse(json);
    const token = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/)?.[1];
    if (!cart.token && token) cart.token = decodeURIComponent(token);
    const codes = JSON.parse(doc.querySelector("[data-sp-cart-codes]")?.textContent || "[]");
    if (!cart.discount_codes) cart.discount_codes = codes.map((code) => ({ code, applicable: true }));
    return cart;
  } catch {
    return null;
  }
}

// One add, shared by the sheet and the size row on cards. The CartLinesUpdateEvent goes out from `source` at once
// (the bag count and the bag update from its promise), and sp-cart-state renders the updated cart into the add
// response, so there is no second /cart.js request (see product-form.js). `holdBag` stops Horizon's "open bag after
// adding" from opening the bag the moment the response lands: the caller opens it after its "Added" moment. (An add
// from the sheet is already held: the bag waits for a modal it starts in to close.)
async function postAdd(source, variantId, { holdBag = false } = {}) {
  const sections = [
    ...[...document.querySelectorAll("cart-items-component[data-section-id]")].map(
      (element) => element.dataset.sectionId,
    ),
    "sp-cart-state",
  ];
  const deferred = CartLinesUpdateEvent.createPromise();
  const autoOpen = holdBag ? document.querySelector("cart-drawer-component[auto-open]") : null;
  autoOpen?.removeAttribute("auto-open"); // read once, while the event below is dispatched (cart-drawer.js)
  try {
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: "product",
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      }),
    );
  } finally {
    autoOpen?.setAttribute("auto-open", "");
  }
  try {
    const response = await fetch(`${rootUrl}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections.join(","),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    const cart = cartFromSections(result.sections) ?? (await (await fetch(`${rootUrl}cart.js`)).json());
    const failed = !response.ok || Boolean(result.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: result.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: "sp-quick-add",
        didError: failed,
      },
    });
    return {
      failed,
      message: failed ? result.description || result.message || "Sorry, this size could not be added." : "",
    };
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

// Shopify's refusal in the store's words. Its own messages say "cart" (e.g. "You can't add more … to the cart." when
// the last ones are already in the bag); the store says "bag".
function bagMessage(message, size, colour) {
  const where = colour ? ` in ${colour}` : "";
  if (/sold out/i.test(message)) return `Sorry, ${size} has just sold out${where}.`;
  if (/\bcart\b/i.test(message)) return `Sorry, there are no more ${size}${where} to add.`;
  return message;
}

async function addToBag(sizeButton) {
  adding = true;
  const status = sheetPart("status");
  const size = sizeButton.dataset.size;
  const description = [current.colour, size].filter(Boolean).join(", ");
  // Same frame as the tap: the size fills, the others step back, and the wait is named.
  sheet.dataset.busy = "true";
  sizeButton.classList.add("is-adding");
  sizeButton.setAttribute("aria-busy", "true");
  status.textContent = `Adding size ${size} to your bag…`;

  try {
    const { failed, message } = await postAdd(sheet, sizeButton.dataset.variantId);
    if (failed) {
      // Sold out since the page loaded: say so and mark it, rather than leave it looking available.
      if (/sold out/i.test(message)) {
        const variant = current.variants.find((candidate) => String(candidate.id) === sizeButton.dataset.variantId);
        if (variant) variant.available = false;
        setSizeStock(sizeButton, false);
      }
      status.textContent = bagMessage(message, size, current.colour);
      return;
    }
    sizeButton.classList.replace("is-adding", "is-added");
    sizeButton.removeAttribute("aria-busy");
    sizeButton.insertAdjacentHTML(
      "beforeend",
      '<svg class="sp-tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.1L13 4.6"/></svg>',
    );
    status.textContent = `Added to your bag: ${description}.`;
    // The "Added" moment (black fill, white tick) holds about 1.5 s, then the bag takes over. With reduced motion
    // there is no fade (sp-commerce.css), only a shorter pause to read "Added".
    await new Promise((resolve) => setTimeout(resolve, reducedMotion.matches ? 1000 : 1500));
    if (sheet.open) sheet.close();
    // With the theme's "open bag after adding" setting on, the theme opens it when the sheet closes. A sheet opened
    // from inside the bag ("Similar styles", Round 10A) closes back onto the open bag: the toggle would shut it, and
    // the bag button that opened the sheet was redrawn by the bag's update, so focus goes to the bag's close button
    // (where it lands when the bag opens) instead of falling out to the page.
    const openBagDrawer = bagDrawer()?.hasAttribute("open") ? bagDrawer() : null;
    if (openBagDrawer && !openBagDrawer.contains(document.activeElement)) {
      openBagDrawer.querySelector(".theme-drawer__close-button")?.focus({ preventScroll: true });
    }
    if (!document.querySelector("cart-drawer-component[auto-open]") && !openBagDrawer) {
      // Found by what it does, not by its label (now "Bag"); no CSS selector can match an on:click attribute.
      [...document.querySelectorAll("#header-group header-actions button")]
        .find((button) => button.getAttribute("on:click") === "#cart-drawer/toggle")
        ?.click();
    }
  } catch {
    status.textContent = "Sorry, that did not go through. Please check your connection and try again.";
  } finally {
    adding = false;
    delete sheet.dataset.busy;
    sizeButton.removeAttribute("aria-busy");
    sizeButton.classList.remove("is-adding", "is-added");
    sizeButton.querySelector(".sp-tick")?.remove();
  }
}

document.addEventListener("click", (event) => {
  const button = event.target instanceof Element ? event.target.closest("[data-sp-qa]") : null;
  if (!button) return;
  event.preventDefault();
  openSheet(button);
});

/* Hover quick add (mouse only): resting on a card shows a row of sizes for the colour on show over the bottom of the
   photo, and a click on a size adds it, with the sheet's "Added" moment, then the bag opens (as after the sheet).
   The row is built on the first hover from the card's own data (data-sp-variants), so it appears at once; sold-out
   sizes are crossed out and cannot be added; Shopify's stock is fetched only when the pointer stays on a card. Its
   buttons are left out of the Tab order: keyboard, touch and screen-reader shoppers have the bag button and sheet,
   which do the same. The second photo is added to the card on the same first hover (see sp-commerce.css). */
const finePointer = matchMedia("(hover: hover) and (pointer: fine)");
let cardStock = new WeakMap(); // card -> Map(variant id -> available): Shopify's stock, and sizes found sold out on add
let stockAsked = new WeakSet(); // cards whose stock has been asked of Shopify
const noteTimers = new WeakMap();
// A page restored by Back may be hours old (as for the sheet above): ask Shopify for stock again.
window.addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  cardStock = new WeakMap();
  stockAsked = new WeakSet();
});

function ensureSecondPhoto(card) {
  const photo = card.querySelector(".sp-card-photo");
  if (!photo || photo.querySelector(".sp-card-alt")) return;
  // The second photo of the colour on show (none if that colour has none). Without a current swatch (no colours, or a
  // colour past the three swatches), the photo link's own second photo.
  const dot = card.querySelector(`${dotSelector}[aria-current]`);
  const back = dot ? dot.dataset.spBack : photo.dataset.spAlt;
  if (!back) return;
  const image = document.createElement("img");
  image.className = "sp-card-alt";
  image.alt = "";
  image.decoding = "async";
  image.style.objectPosition = (dot ? dot.dataset.spBackPos : photo.dataset.spAltPos) || "";
  // A second photo that fails to load steps aside; the main photo shows through.
  image.addEventListener("error", () => {
    image.hidden = true;
  });
  holdUntilLoaded(image);
  image.sizes = photo.querySelector(".sp-card-main")?.sizes || "25vw";
  image.srcset = srcsetFor(back);
  image.src = resized(back, 540);
  photo.append(image);
}

function quickVariants(card) {
  const colour = card.dataset.spColour || "";
  const stock = cardStock.get(card);
  return variantsFromCard(card)
    .filter((variant) => !colour || variant.colour === colour)
    .map((variant) =>
      stock?.has(String(variant.id)) ? { ...variant, available: stock.get(String(variant.id)) } : variant,
    );
}

function quickNote(card, text) {
  const row = card.querySelector(".sp-card-quick");
  if (!row) return;
  clearTimeout(noteTimers.get(card));
  const note = row.querySelector("[data-sp-quick-note]");
  if (text) {
    row.dataset.spNote = "";
    note.textContent = text;
  } else {
    delete row.dataset.spNote;
    note.textContent = "Quick add";
  }
}

function renderCardSizes(card) {
  const row = card.querySelector(".sp-card-quick");
  if (!row || card.dataset.spBusy !== undefined) return;
  const colour = card.dataset.spColour || "";
  const title = card.querySelector(".sp-card-copy h3")?.textContent.trim() || "";
  row.setAttribute("aria-label", `Add ${title}${colour ? ` in ${colour}` : ""} to bag`);
  row.querySelector("[data-sp-quick-colour]").textContent = colour;
  row.querySelector(".sp-card-sizes").replaceChildren(
    ...quickVariants(card).map((variant) => {
      const button = document.createElement("button");
      button.type = "button";
      button.tabIndex = -1;
      button.dataset.variantId = String(variant.id);
      button.dataset.size = variant.size || "One size";
      const label = document.createElement("span");
      label.textContent = button.dataset.size;
      button.append(label);
      setSizeStock(button, variant.available);
      return button;
    }),
  );
}

function ensureQuickRow(card) {
  if (card.querySelector(".sp-card-quick")) return;
  const media = card.querySelector(".sp-card-media");
  // Only cards of real products that can be added (the same cards that have a bag button).
  // A product with no size choice keeps its bag button instead (sp-commerce.css): a row would hold one button.
  if (
    !media ||
    card.hasAttribute("data-sp-one-size") ||
    !card.querySelector("[data-sp-qa]") ||
    !variantsFromCard(card).length
  )
    return;
  const row = document.createElement("div");
  row.className = "sp-card-quick";
  row.setAttribute("role", "group");
  row.innerHTML =
    '<p class="sp-card-quick-head"><span data-sp-quick-note>Quick add</span><span data-sp-quick-colour></span></p><div class="sp-card-sizes"></div>';
  media.append(row);
  renderCardSizes(card);
}

// Shopify's stock for a card the pointer has stayed on (the page may have been open a while). Sizes update in place.
function refreshCardStock(card) {
  const handle = card.querySelector("[data-sp-qa]")?.dataset.spQa;
  if (!handle || stockAsked.has(card)) return;
  stockAsked.add(card);
  loadProduct(handle)
    .then((product) => {
      const stock = cardStock.get(card) || new Map();
      // Shopify's answer, except sizes an add has just found sold out (newer than this answer).
      variantsFromProduct(product).forEach((variant) => {
        if (stock.get(String(variant.id)) !== false) stock.set(String(variant.id), variant.available);
      });
      cardStock.set(card, stock);
      if (card.dataset.spBusy !== undefined) return;
      card.querySelectorAll(".sp-card-sizes [data-variant-id]").forEach((button) => {
        if (stock.has(button.dataset.variantId)) setSizeStock(button, stock.get(button.dataset.variantId));
      });
    })
    .catch(() => {});
}

let stockTimer;
document.addEventListener(
  "pointerover",
  (event) => {
    if (!["mouse", "pen"].includes(event.pointerType) || !finePointer.matches || !(event.target instanceof Element))
      return;
    const card = event.target.closest("[data-sp-card]");
    if (!card) return;
    ensureSecondPhoto(card);
    ensureQuickRow(card);
    clearTimeout(stockTimer);
    stockTimer = setTimeout(() => {
      if (card.matches(":hover")) refreshCardStock(card);
    }, 400);
  },
  { passive: true },
);

let pageVisits = 0;
window.addEventListener("pagehide", () => (pageVisits += 1));

function openBag() {
  const drawer = document.getElementById("cart-drawer");
  if (drawer && typeof drawer.open === "function") drawer.open();
}

async function addFromCard(button) {
  const card = button.closest("[data-sp-card]");
  const size = button.dataset.size;
  const colour = card.dataset.spColour || "";
  if (adding) {
    if (!button.classList.contains("is-adding")) {
      quickNote(card, "One moment…");
      noteTimers.set(
        card,
        setTimeout(() => quickNote(card, ""), 2400),
      );
    }
    return;
  }
  if (button.classList.contains("is-sold-out")) {
    const text = `${size} is sold out${colour ? ` in ${colour}` : ""}.`;
    quickNote(card, `${size} is sold out`);
    announceStatus(text);
    noteTimers.set(
      card,
      setTimeout(() => quickNote(card, ""), 2400),
    );
    return;
  }
  adding = true;
  card.dataset.spBusy = "";
  button.classList.add("is-adding");
  button.setAttribute("aria-busy", "true");
  quickNote(card, `Adding ${size}…`);
  let added = false;
  try {
    const { failed, message } = await postAdd(card, button.dataset.variantId, { holdBag: true });
    if (failed) {
      if (/sold out/i.test(message)) {
        const stock = cardStock.get(card) || new Map();
        stock.set(button.dataset.variantId, false);
        cardStock.set(card, stock);
        setSizeStock(button, false);
      }
      const text = bagMessage(message, size, colour);
      quickNote(card, text);
      announceStatus(text);
      return;
    }
    added = true;
    button.classList.replace("is-adding", "is-added");
    button.removeAttribute("aria-busy");
    button.insertAdjacentHTML(
      "beforeend",
      '<svg class="sp-tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.1L13 4.6"/></svg>',
    );
    quickNote(card, "Added to bag");
    // Same moment as the sheet: "Added" for about 1.5 s (1 s with reduced motion), then the bag.
    const visit = pageVisits;
    await new Promise((resolve) => setTimeout(resolve, reducedMotion.matches ? 1000 : 1500));
    if (visit !== pageVisits) return; // the shopper left during the moment
    // The bag gives focus back to what had it when it opened. The size row hides once the pointer is on the bag, so
    // that is the card's photo link instead: closing the bag lands back on this card.
    card.querySelector(".sp-card-photo")?.focus({ preventScroll: true });
    openBag();
  } catch {
    const text = "Sorry, that did not go through. Please try again.";
    quickNote(card, text);
    announceStatus(text);
  } finally {
    adding = false;
    delete card.dataset.spBusy;
    button.removeAttribute("aria-busy");
    button.classList.remove("is-adding", "is-added");
    button.querySelector(".sp-tick")?.remove();
    renderCardSizes(card); // a colour or stock change made while it was busy
    if (added) quickNote(card, "");
    else
      noteTimers.set(
        card,
        setTimeout(() => quickNote(card, ""), 3200),
      );
  }
}

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || event.defaultPrevented) return;
  const card = document.querySelector("[data-sp-card]:hover");
  if (card?.querySelector(".sp-card-quick")) card.dataset.spQuickAway = "";
});
document.addEventListener(
  "pointerout",
  (event) => {
    const card = event.target instanceof Element ? event.target.closest("[data-sp-card][data-sp-quick-away]") : null;
    if (card && !(event.relatedTarget instanceof Node && card.contains(event.relatedTarget)))
      delete card.dataset.spQuickAway;
  },
  { passive: true },
);

document.addEventListener("click", (event) => {
  const button =
    event.target instanceof Element ? event.target.closest(".sp-card-sizes button[data-variant-id]") : null;
  if (!button) return;
  event.preventDefault();
  addFromCard(button);
});

/* Product intro (phones): show three lines with "Learn more". Round 5: only when that hides a real amount (more than
   about two further lines); a short intro is shown whole, so "Learn more" never stacks over the Size guide link to
   reveal one line. */
function clampIntro() {
  const intro = document.querySelector(".product-information .sp-product-intro");
  if (!intro || intro.dataset.spClamp || !phone.matches) return;
  intro.dataset.spClamp = "true";
  intro.classList.add("sp-clamped");
  if (intro.scrollHeight <= intro.clientHeight * 1.6) {
    intro.classList.remove("sp-clamped");
    return;
  }
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "sp-learn-more sp-m-only";
  toggle.setAttribute("aria-expanded", "false");
  toggle.textContent = "Learn more";
  toggle.addEventListener("click", () => {
    const open = intro.classList.toggle("sp-clamped") === false;
    toggle.setAttribute("aria-expanded", String(open));
    toggle.textContent = open ? "Show less" : "Learn more";
  });
  intro.after(toggle);
}

/* Category chips (phones): keep the current category in view. */
function revealCurrentChip() {
  if (!phone.matches) return;
  const current = document.querySelector(
    '.sp-filter-links [aria-current="page"], .sp-filter-buttons [aria-pressed="true"]',
  );
  const row = current?.parentElement;
  if (current && row) row.scrollLeft = current.offsetLeft - 18;
}

clampIntro();
revealCurrentChip();
document.addEventListener("shopify:section:load", () => {
  clampIntro();
  revealCurrentChip();
});

/* Motion tokens shared with CSS (sp-sport.css :root). */
function motionMs(name, fallback) {
  const value = getComputedStyle(html).getPropertyValue(name).trim();
  const ms = value.endsWith("ms") ? parseFloat(value) : value.endsWith("s") ? parseFloat(value) * 1000 : NaN;
  return Number.isFinite(ms) ? ms : fallback;
}

/* Card photo becomes the product photo: hand the clicked card's photo to Horizon's view transition. */
function clearCardTransition() {
  document.querySelectorAll("[data-sp-transition][data-view-transition-triggered]").forEach((photo) => {
    photo.removeAttribute("data-view-transition-triggered");
    photo.removeAttribute("data-view-transition-type");
  });
}

document.addEventListener("click", (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return;
  const link = event.target instanceof Element ? event.target.closest("[data-sp-card] a[href]") : null;
  if (!link) return;
  const photo = link.closest("[data-sp-card]").querySelector(".sp-card-photo[data-sp-transition]");
  if (!photo) return;
  clearCardTransition();
  photo.setAttribute("data-view-transition-type", "product-image-transition");
  photo.setAttribute("data-view-transition-triggered", "true");
});
window.addEventListener("pageshow", clearCardTransition);

/* Photos fade in once loaded, inside their reserved grey frame. The product gallery is left alone. */
const fadeSelector = ".sp-card-photo img";
function fadeIn(image) {
  if (!(image instanceof HTMLImageElement) || image.dataset.spFade) return;
  image.dataset.spFade = "true";
  if (image.complete && image.naturalWidth) return;
  image.classList.add("sp-img-loading");
  const done = () => image.classList.remove("sp-img-loading");
  image.addEventListener("load", done, { once: true });
  image.addEventListener("error", done, { once: true });
  if (image.complete) done();
}
document.querySelectorAll(fadeSelector).forEach(fadeIn);
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(fadeSelector)) fadeIn(node);
      node.querySelectorAll?.(fadeSelector).forEach(fadeIn);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/* Round 10: a row whose photos are not part of the page's first load (since Round 10A the product page's "Similar
   styles", snippets/sp-similar-styles.liquid). Its card photos arrive as a blank pixel with the real address in
   data-sp-src / data-sp-srcset, and load together when the row comes within 300px of the window, so the cards further
   along its sideways row on phones are ready when swiped to. They fade in like every card photo. Without IntersectionObserver they load at once. */
function loadHeldPhotos(row) {
  row.querySelectorAll("img[data-sp-src]").forEach((image) => {
    image.classList.add("sp-img-loading");
    const done = () => image.classList.remove("sp-img-loading");
    image.addEventListener("load", done, { once: true });
    image.addEventListener("error", done, { once: true });
    if (image.dataset.spSrcset) image.srcset = image.dataset.spSrcset;
    image.src = image.dataset.spSrc;
    image.removeAttribute("data-sp-src");
    image.removeAttribute("data-sp-srcset");
  });
}
const heldRows =
  "IntersectionObserver" in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            loadHeldPhotos(entry.target);
          }
        },
        { rootMargin: "0px 0px 300px 0px" },
      )
    : null;
const watchHeldRows = (root = document) =>
  root.querySelectorAll("[data-sp-defer-photos]").forEach((row) => (heldRows ? heldRows.observe(row) : loadHeldPhotos(row)));
watchHeldRows();
document.addEventListener("shopify:section:load", (event) => watchHeldRows(event.target));

/* One selection language: a single fill travels to the chosen chip, like the size picker. */
function initChipTracks(root = document) {
  root.querySelectorAll(".sp-filter-buttons, .sp-filter-links, .sp-list-chips").forEach((track) => {
    if (track.dataset.spPill || track.closest("noscript")) return;
    track.dataset.spPill = "true";
    track.classList.add("sp-chip-track");
    const pill = document.createElement("span");
    pill.className = "sp-chip-pill";
    pill.setAttribute("aria-hidden", "true");
    track.prepend(pill);
    const current = () => track.querySelector('[aria-pressed="true"], [aria-current="page"], [aria-current="true"]');
    let placed;
    const place = (chip, animate) => {
      placed = chip;
      if (!chip) {
        pill.style.opacity = "0";
        return;
      }
      if (!animate) pill.style.transition = "none";
      pill.style.opacity = "1";
      pill.style.width = `${chip.offsetWidth}px`;
      pill.style.height = `${chip.offsetHeight}px`;
      pill.style.transform = `translate(${chip.offsetLeft}px, ${chip.offsetTop}px)`;
      if (!animate) {
        void pill.offsetWidth;
        pill.style.transition = "";
      }
    };
    const initial = current();
    place(initial, false);
    track.addEventListener("click", (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const chip = event.target instanceof Element ? event.target.closest("button, a") : null;
      if (!chip || !track.contains(chip)) return;
      // Round 22: a chip that is a filter (data-sp-chip) is marked by sp-collection.js once its results are in.
      if (chip.tagName === "A" && !chip.hasAttribute("data-sp-chip")) {
        // The link is about to become the current page; mark it now so text and fill stay paired.
        track.querySelectorAll("a[aria-current]").forEach((link) => link.removeAttribute("aria-current"));
        chip.setAttribute("aria-current", "page");
      }
      requestAnimationFrame(() => place(chip, true));
    });
    window.addEventListener("pageshow", (event) => {
      if (!event.persisted || !initial || initial.tagName !== "A") return;
      track.querySelectorAll("a[aria-current]").forEach((link) => link.removeAttribute("aria-current"));
      initial.setAttribute("aria-current", "page");
      place(initial, false);
    });
    new ResizeObserver(() => place(current(), false)).observe(track);
    // sp-collection.js (async, so it may run before or after this) sets aria-pressed from the address: follow it,
    // measuring only when the pressed chip actually changes (every filter change rewrites aria-pressed).
    new MutationObserver(() => {
      if (current() !== placed) place(current(), true);
    }).observe(track, { subtree: true, attributes: true, attributeFilter: ["aria-pressed", "aria-current"] });
  });
}

/* Add-to-bag confirmation: announce it and point at the new line in the bag. */
const live = document.createElement("p");
live.className = "sp-visually-hidden";
live.setAttribute("role", "status");
live.setAttribute("aria-live", "polite");
document.body.append(live);
// Cleared first, so the same words twice in a row are still announced.
function announceStatus(text) {
  live.textContent = "";
  requestAnimationFrame(() => (live.textContent = text));
}

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  if (event.action !== "add") return;
  let result;
  try {
    result = await event.promise;
  } catch {
    return;
  }
  if (!result || result.detail?.didError) return;
  announceStatus("Added to your bag.");
  const key = result.detail?.items?.[0]?.key;
  if (!key || reducedMotion.matches) return;
  const started = performance.now();
  const highlight = () => {
    const row = [...document.querySelectorAll(".cart-items__table-row[data-key]")].find(
      (candidate) => candidate.dataset.key === key && candidate.offsetParent,
    );
    if (row) {
      row.classList.remove("sp-line-new");
      void row.offsetWidth;
      row.classList.add("sp-line-new");
      row.addEventListener("animationend", () => row.classList.remove("sp-line-new"), { once: true });
    } else if (performance.now() - started < 2000) {
      setTimeout(highlight, 120);
    }
  };
  highlight();
});

initChipTracks();
document.addEventListener("shopify:section:load", (event) => initChipTracks(event.target));

/* Press response on touch: scale to 0.97 on the response token, no bounce. Only `scale` is animated,
   so each control keeps its own colour and border transitions. */
const pressSelector = [
  ".sp-cta",
  ".button",
  ".add-to-cart-button",
  ".shopify-payment-button__button",
  ".sp-qa",
  ".sp-sheet-sizes button",
  ".sp-chip-track > button",
  ".sp-chip-track > a",
  ".product-information .variant-option__button-label",
  ".sp-card-dots a",
  ".sp-menu-search button",
  ".sp-sheet-colour-list button",
  ".sp-filter-open",
  ".sp-applied-chip",
].join(",");
const pressed = new WeakMap();

function pressTarget(element) {
  // Colour dots shrink their visible circle, not their 44px tap area.
  return element.matches(".sp-card-dots a") ? element.querySelector(":scope > span") || element : element;
}

function release(element) {
  const animation = pressed.get(element);
  if (!animation) return;
  pressed.delete(element);
  const target = pressTarget(element);
  const back = target.animate(
    [{ scale: getComputedStyle(target).scale === "none" ? 1 : getComputedStyle(target).scale }, { scale: 1 }],
    {
      duration: motionMs("--sp-response", 120),
      easing: "cubic-bezier(.22,1,.36,1)",
    },
  );
  animation.cancel();
  back.finished.then(
    () => back.cancel(),
    () => {},
  );
}

document.addEventListener(
  "pointerdown",
  (event) => {
    if (event.pointerType === "mouse" || reducedMotion.matches || !(event.target instanceof Element)) return;
    const element = event.target.closest(pressSelector);
    if (!element || element.matches(':disabled,[aria-disabled="true"]')) return;
    const animation = pressTarget(element).animate([{ scale: 1 }, { scale: 0.97 }], {
      duration: motionMs("--sp-response", 120),
      easing: "cubic-bezier(.22,1,.36,1)",
      fill: "forwards",
    });
    pressed.set(element, animation);
    const end = () => {
      release(element);
      ["pointerup", "pointercancel", "pointerleave"].forEach((type) => element.removeEventListener(type, end));
    };
    ["pointerup", "pointercancel", "pointerleave"].forEach((type) => element.addEventListener(type, end));
  },
  { passive: true },
);

/* Shareable product address: it always carries the chosen colour, and a size only once the shopper picked one.
   Before a size is chosen, Shopify's option_values parameter holds the colour alone (a variant id would imply a size). */
function syncProductUrl() {
  const picker = document.querySelector(".product-information variant-picker");
  if (!picker) return;
  const url = new URL(window.location.href);
  if (html.classList.contains("sp-size-unset")) {
    const colour = picker.querySelector(".sp-colour-option input:checked");
    url.searchParams.delete("variant");
    if (colour?.dataset.optionValueId) url.searchParams.set("option_values", colour.dataset.optionValueId);
  } else {
    // A size is chosen. Horizon writes ?variant= when a choice changes the variant; choosing the size it had already
    // checked behind the size gate changes nothing, so it is written here and shared links keep the size.
    if (!url.searchParams.has("variant")) {
      const variant = picker.querySelector("fieldset:not(.sp-colour-option) input:checked")?.dataset.variantId;
      if (variant) url.searchParams.set("variant", variant);
    }
    if (url.searchParams.has("variant")) url.searchParams.delete("option_values");
  }
  if (url.href !== window.location.href) history.replaceState(history.state, "", url.toString());
}

let urlSyncTimer;
const scheduleUrlSync = () => {
  clearTimeout(urlSyncTimer);
  // Runs after Horizon writes ?variant= (it yields to the main thread first).
  urlSyncTimer = setTimeout(syncProductUrl, 150);
};
document.addEventListener("change", (event) => {
  if (event.target instanceof Element && event.target.closest(".product-information variant-picker")) scheduleUrlSync();
});
document.addEventListener("variant:update", scheduleUrlSync);
// A colour change redraws the buy buttons: give them their "Select a size" name again while no size is chosen.
document.addEventListener("variant:update", () => requestAnimationFrame(syncAddLabels));
syncProductUrl();

/* Product page sizes follow the chosen colour. Size is these products' first option, and Shopify marks a first-option
   value as available when any colour has it in stock, so Horizon only showed a size as sold out after it was picked.
   Here each size with no stock in the chosen colour is crossed out straight away, with Horizon's own markup and styles
   (data-option-available, its strikethrough line, "Sold out" in the name). Stock comes from snippets/sp-variant-stock
   (rendered by the product details block). Horizon redraws the picker after every change, so this runs again after
   each redraw (in the same task, before anything is painted). A crossed-out size can still be chosen: Horizon then
   shows its sold-out button, as before, and the address still carries the variant. */
const strikeMarkup =
  '<svg viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" class="variant-option__strikethrough" aria-hidden="true" data-sp-strike><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"/><line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke"/></svg>';

function markSizesForColour() {
  const data = document.querySelector(".product-information [data-sp-variant-stock]");
  const fieldset = sizeFieldset();
  const colour = document.querySelector(".product-information .sp-colour-option input:checked")?.value;
  if (!data || !fieldset || !colour) return;
  let stock;
  try {
    stock = JSON.parse(data.textContent);
  } catch {
    return;
  }
  fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
    const inStock = stock.some(
      ([size, variantColour, available]) => size === input.value && variantColour === colour && available === 1,
    );
    const ours = input.dataset.spMarked === "true";
    if (!inStock && input.dataset.optionAvailable !== "false") {
      input.dataset.spMarked = "true";
      input.dataset.optionAvailable = "false";
      input.setAttribute("aria-disabled", "true");
      input.setAttribute("aria-label", `${input.value} - Sold out`);
      if (!input.parentElement.querySelector(".variant-option__strikethrough"))
        input.parentElement.insertAdjacentHTML("beforeend", strikeMarkup);
    } else if (inStock && ours) {
      // Only undo what this script did: sizes Horizon itself marks sold out (no stock in any colour) stay as they are.
      delete input.dataset.spMarked;
      input.dataset.optionAvailable = "true";
      input.removeAttribute("aria-disabled");
      input.setAttribute("aria-label", input.value);
      input.parentElement.querySelector("[data-sp-strike]")?.remove();
    }
  });
  showLowStock(stock, colour, fieldset, data.dataset.spLowText || "");
}

/* "Only a few left" under the sizes when the chosen size of the chosen colour is low (Round 3A; the fourth value of
   each sp-variant-stock entry, from the store's tracked stock). A status line, so screen readers hear it; ink, not
   neon. Horizon redraws the picker after each change and this runs again after each redraw. */
function showLowStock(stock, colour, fieldset, text) {
  let note = fieldset.parentElement.querySelector(":scope > .sp-stock-note");
  const size = fieldset.querySelector('input[type="radio"]:checked');
  const sizeChosen = size && !document.documentElement.classList.contains("sp-size-unset");
  const low = Boolean(
    text &&
      sizeChosen &&
      stock.some(([s, c, available, isLow]) => s === size.value && c === colour && available === 1 && isLow === 1),
  );
  if (!note) {
    if (!low) return;
    note = document.createElement("p");
    note.className = "sp-stock-note";
    note.setAttribute("role", "status");
    fieldset.after(note);
  }
  const next = low ? text : "";
  if (note.textContent !== next) note.textContent = next;
  note.hidden = !low;
}

const productInformation = document.querySelector(".product-information");
if (productInformation) {
  markSizesForColour();
  document.addEventListener("change", (event) => {
    if (event.target instanceof Element && event.target.closest(".product-information .sp-colour-option"))
      markSizesForColour();
  });
  new MutationObserver(markSizesForColour).observe(productInformation, { subtree: true, childList: true });
}

/* Live suggestions under the header and menu search fields. They come from the theme's search section, so
   suggestions, the results page and shared search links all understand the same words, typos and colours. */
const suggestionCache = new Map();

function initSuggestions(input) {
  const form = input.closest("form");
  if (!form || input.dataset.spSuggest !== undefined) return;
  input.dataset.spSuggest = "";
  const panel = document.createElement("div");
  panel.className = "sp-suggest";
  panel.id = `${input.id}-suggestions`;
  panel.hidden = true;
  const status = document.createElement("p");
  status.className = "sp-visually-hidden";
  status.setAttribute("role", "status");
  // The header panel has a place for them under its search bar (sections/sp-header.liquid); the phone menu's go
  // straight under its field.
  const slot = form.closest("dialog")?.querySelector("[data-sp-suggest-slot]");
  if (slot) slot.prepend(panel, status);
  else form.after(panel, status);
  input.setAttribute("aria-controls", panel.id);
  // An empty search goes nowhere (no browser "fill out this field" bubble).
  form.addEventListener("submit", (event) => {
    if (!input.value.trim()) {
      event.preventDefault();
      input.focus();
    }
  });
  let timer;
  let controller;

  const show = (query, html) => {
    if (input.value.trim() !== query) return;
    panel.innerHTML = html;
    panel.hidden = !html;
    // Collections first, then styles (a collection's own styles count when it matched and no style did).
    const inner = panel.querySelector("[data-count]");
    const collections = Number(inner?.dataset.collections || 0);
    const styles = Number(inner?.dataset.count || 0) || panel.querySelectorAll(".sp-suggest-list > li").length;
    const found = [];
    if (collections) found.push(`${collections} ${collections === 1 ? "collection" : "collections"}`);
    if (styles) found.push(`${styles} ${styles === 1 ? "style" : "styles"}`);
    status.textContent = found.length ? `${found.join(" and ")} found` : "No matching styles";
  };

  const load = async (query) => {
    if (suggestionCache.has(query)) return show(query, suggestionCache.get(query));
    controller?.abort();
    controller = new AbortController();
    const url = new URL(`${rootUrl}search/suggest`, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("section_id", "sp-search-suggest");
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", "6");
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;
      const page = new DOMParser().parseFromString(await response.text(), "text/html");
      const html = page.querySelector(".sp-suggest-inner")?.outerHTML || "";
      suggestionCache.set(query, html);
      show(query, html);
    } catch {
      // A newer keystroke aborted this request, or the network failed: the form still submits to the results page.
    }
  };

  input.addEventListener("input", () => {
    clearTimeout(timer);
    const query = input.value.trim();
    if (query.length < 2) {
      controller?.abort();
      panel.hidden = true;
      panel.replaceChildren();
      status.textContent = "";
      return;
    }
    timer = setTimeout(() => load(query), 180);
  });

  // Arrow keys move between the field and the suggestions (collections, then styles, then "See all"); before anything
  // is typed, between the field and the popular searches and quick links. Escape goes back to the field.
  const start = slot?.querySelector(".sp-search-start");
  const links = () =>
    [...(panel.hidden && start ? start : panel).querySelectorAll("a[href]")].filter(
      (link) => link.getClientRects().length,
    );
  input.addEventListener("keydown", (event) => {
    // Escape in the field closes the panel at once (a search field would otherwise spend one Escape clearing the
    // words); the dialog's own closing runs (sp-sport.js listens for its cancel event).
    if (event.key === "Escape") {
      const dialog = input.closest("dialog[open]");
      if (!dialog) return;
      event.preventDefault();
      dialog.dispatchEvent(new Event("cancel", { cancelable: true }));
      return;
    }
    if (event.key !== "ArrowDown" || (panel.hidden && !start) || !links().length) return;
    event.preventDefault();
    links()[0].focus();
  });
  (slot || panel).addEventListener("keydown", (event) => {
    if (event.target === input) return;
    const list = links();
    const index = list.indexOf(document.activeElement);
    if (event.key === "ArrowDown" && index < list.length - 1) {
      event.preventDefault();
      list[index + 1].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      (index > 0 ? list[index - 1] : input).focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      input.focus();
    }
  });
}
document.querySelectorAll("#sp-query, #sp-menu-query").forEach(initSuggestions);

/* Product gallery settles softly after a colour change (moved here from sp-commerce.js to save a request on every page). */
document.addEventListener(StandardEvents.productSelect, (event) => {
  if (!(event.target instanceof Element) || event.target.closest("product-card")) return;
  if (!event.promise || reducedMotion.matches) return;
  const section = event.target.closest(".shopify-section");
  event.promise
    .then(() =>
      requestAnimationFrame(() => {
        const gallery = section?.querySelector("media-gallery");
        if (gallery) gallery.animate([{ opacity: 0.65 }, { opacity: 1 }], { duration: 180, easing: "ease-out" });
      }),
    )
    .catch(() => {
      /* Native components retain their own error handling. */
    });
});

/* Product gallery: the phone swipe row's position indicator, silent videos that only play while on screen (a still
   poster with reduced motion), and a desktop buy panel that only stays in place while it fits the screen. */
const galleryVisible = new WeakMap();

function setGalleryMediaPlaying(target) {
  const play = galleryVisible.get(target) && !reducedMotion.matches && !document.hidden;
  if (target instanceof HTMLVideoElement) {
    target.muted = true;
    if (play)
      target.play().then(
        () => target.classList.add("is-playing"),
        () => {},
      );
    else if (!target.paused) target.pause();
    return;
  }
  let frame = target.querySelector("iframe");
  if (!frame) {
    if (!play) return;
    // The embed is only created once the tile is on screen: no player scripts load for videos nobody sees.
    frame = document.createElement("iframe");
    frame.src = target.dataset.spGalleryEmbed;
    frame.title = target.dataset.spTitle || "Product video";
    frame.allow = "autoplay; encrypted-media";
    frame.tabIndex = -1;
    frame.setAttribute("aria-hidden", "true");
    frame.addEventListener("load", () => target.classList.add("is-playing"), { once: true });
    target.append(frame);
    return;
  }
  const command =
    target.dataset.spHost === "vimeo"
      ? { method: play ? "play" : "pause" }
      : { event: "command", func: play ? "playVideo" : "pauseVideo", args: [] };
  frame.contentWindow?.postMessage(JSON.stringify(command), "*");
}

const galleryObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      galleryVisible.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.5);
      setGalleryMediaPlaying(entry.target);
    }
  },
  { threshold: [0, 0.5] },
);

// The photo in view: one tile's step is the distance between two tiles (a whole row on phones, 60% of it plus the gap
// on portrait tablets, Round 8A). At the row's end the last photo counts as current, as it may never reach the start.
function updateGalleryPosition(row) {
  const [first, second] = row.children;
  const step = second ? second.offsetLeft - first.offsetLeft : row.clientWidth;
  const atEnd = row.scrollLeft >= row.scrollWidth - row.clientWidth - 2;
  const index = atEnd ? row.children.length - 1 : Math.round(row.scrollLeft / Math.max(step, 1));
  row.parentElement
    ?.querySelectorAll("[data-sp-gallery-position] span")
    .forEach((segment, i) => segment.classList.toggle("is-current", i === index));
}

// A buy panel taller than the screen scrolls with the page until its bottom edge reaches the bottom of the screen, then
// stays there while the photos continue: every part of it stays reachable.
function guardStickyDetails() {
  document.querySelectorAll(".product-information .product-details.sticky-content--desktop").forEach((details) => {
    details.style.top = "";
    const headerTop = parseFloat(getComputedStyle(details).top) || 0;
    const overflow = details.offsetHeight - (window.innerHeight - headerTop);
    details.classList.toggle("sp-details-tall", overflow > 0);
    if (overflow > 0) details.style.top = `${Math.round(window.innerHeight - details.offsetHeight)}px`;
  });
}
const detailsResize = new ResizeObserver(guardStickyDetails);

function initGallery() {
  document
    .querySelectorAll(".sp-gallery-video:not([data-sp-watched]), [data-sp-gallery-embed]:not([data-sp-watched])")
    .forEach((media) => {
      media.dataset.spWatched = "";
      if (media instanceof HTMLVideoElement) media.setAttribute("aria-hidden", "true");
      galleryObserver.observe(media);
    });
  document.querySelectorAll("[data-sp-gallery-row]:not([data-sp-ready])").forEach((row) => {
    row.dataset.spReady = "";
    let frame = 0;
    row.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => updateGalleryPosition(row));
      },
      { passive: true },
    );
  });
  document
    .querySelectorAll(".product-information .product-details.sticky-content--desktop:not([data-sp-watched])")
    .forEach((details) => {
      details.dataset.spWatched = "";
      detailsResize.observe(details);
    });
}

let galleryScan = 0;
// Colour changes re-render the gallery: pick up its new tiles and videos.
new MutationObserver(() => {
  cancelAnimationFrame(galleryScan);
  galleryScan = requestAnimationFrame(initGallery);
}).observe(document.body, { childList: true, subtree: true });
const replayGallery = () =>
  document
    .querySelectorAll("[data-sp-watched].sp-gallery-video, [data-sp-watched][data-sp-gallery-embed]")
    .forEach(setGalleryMediaPlaying);
reducedMotion.addEventListener("change", replayGallery);
document.addEventListener("visibilitychange", replayGallery);
window.addEventListener("resize", guardStickyDetails, { passive: true });
initGallery();

/* Size guide dialog. The link is an ordinary link to the guide page (it still works without scripts); sp-sport.js
   opens the dialog on tap. The guide's section of that page is fetched once the product page has finished loading
   and the browser is idle, so it is already in the dialog when the shopper taps. Hovering, focusing or touching the
   link fetches it sooner if the shopper gets there first, and is the only trigger when Data Saver is on. */
const sizeGuideRequests = new Map();

function loadSizeGuide(link, priority = "auto") {
  const url = new URL(link.href, window.location.href);
  const anchor = url.hash.slice(1) || "size-guide";
  url.hash = "";
  if (!sizeGuideRequests.has(url.href)) {
    const request = fetch(url.href, { credentials: "same-origin", priority })
      .then((response) => {
        if (!response.ok) throw new Error(`Size guide ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const page = new DOMParser().parseFromString(html, "text/html");
        const start = page.getElementById(anchor);
        if (!start) throw new Error("Size guide section missing");
        // The guide runs from its heading block to the next section that has its own anchor.
        const parts = [start];
        for (
          let next = start.nextElementSibling;
          next && !next.classList.contains("sp-info-block--anchor");
          next = next.nextElementSibling
        )
          parts.push(next);
        const holder = document.createElement("div");
        parts.forEach((part) => holder.append(document.importNode(part, true)));
        holder.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
        holder.querySelector(".sp-kicker")?.remove();
        holder.querySelector("h2")?.remove(); // "Size guide": the dialog's own title says it.
        holder.querySelectorAll("h2").forEach((heading) => {
          const smaller = document.createElement("h3");
          smaller.innerHTML = heading.innerHTML;
          heading.replaceWith(smaller);
        });
        return holder.innerHTML;
      });
    request.catch(() => sizeGuideRequests.delete(url.href));
    sizeGuideRequests.set(url.href, request);
  }
  return sizeGuideRequests.get(url.href);
}

// The first dialog a page opens costs the browser one-off work (fonts, layers, the opening animation), which made the
// first tap about twice as slow as later ones on a mid-range phone. Laying out a hidden, inert copy once while idle
// does that work in advance. The copy is never opened, shown, focusable or announced, has no ids, and is removed in
// the next frame.
function prewarmSizeGuide(dialog) {
  if (dialog.open || dialog.dataset.prewarmed) return;
  dialog.dataset.prewarmed = "true";
  const copy = dialog.cloneNode(true);
  copy.removeAttribute("id");
  copy.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
  copy.inert = true;
  copy.setAttribute("aria-hidden", "true");
  copy.style.cssText = "display:block;position:fixed;left:0;top:0;visibility:hidden;pointer-events:none";
  document.body.append(copy);
  void copy.offsetHeight;
  requestAnimationFrame(() => setTimeout(() => copy.remove()));
}

function fillSizeGuide(link, priority) {
  const dialog = document.getElementById("sp-size-guide");
  const body = dialog?.querySelector("[data-sp-size-guide-body]");
  if (!body || body.dataset.filled) return;
  loadSizeGuide(link, priority).then(
    (html) => {
      body.innerHTML = html;
      body.dataset.filled = "true";
      whenIdle(() => prewarmSizeGuide(dialog));
    },
    () => {
      body.innerHTML =
        '<p class="sp-size-guide-status" role="status">The size guide could not load here. Open the full guide page below.</p>';
    },
  );
}

const prefetchSizeGuide = (event) => {
  const link = event.target instanceof Element ? event.target.closest("[data-sp-size-guide-link]") : null;
  if (link) fillSizeGuide(link);
};
["pointerover", "touchstart", "focusin", "click"].forEach((type) =>
  document.addEventListener(type, prefetchSizeGuide, { passive: true, capture: true }),
);

// The page behind the guide is held still by sp-sport.css (html:has(dialog:modal)), with the scrollbar's space kept,
// so opening it never shifts the page. The guide's text is fetched while the browser is idle, unless Data Saver is on.
const sizeGuide = document.getElementById("sp-size-guide");
const sizeGuideLink = document.querySelector("[data-sp-size-guide-link]");
const whenIdle = (task) =>
  "requestIdleCallback" in window ? requestIdleCallback(task, { timeout: 3000 }) : setTimeout(task, 1500);

if (sizeGuide) {
  const prepare = () =>
    whenIdle(() => {
      if (sizeGuideLink && !navigator.connection?.saveData) fillSizeGuide(sizeGuideLink, "low");
    });
  if (document.readyState === "complete") prepare();
  else window.addEventListener("load", prepare, { once: true });
}

/* "Added to your bag" in the bag drawer (snippets/sp-bag-status.liquid). When an add succeeds (product page, sticky bar,
   quick add sheet or a card's size row), the drawer is marked before cart-drawer.js opens it, in the same moment, so the
   confirmation line is already there in the drawer's first frame (nothing moves); the new line is the first one. The
   mark goes when the drawer has closed, when the bag is changed any other way, or when the drawer did not open within
   a few seconds (opening after an add turned off), so a later look at the bag never shows an old "Added". */
const bagDrawer = () => document.querySelector("theme-drawer#cart-drawer");
let addedIds = [];
let addedTimer;
function clearAdded() {
  clearTimeout(addedTimer);
  bagDrawer()?.removeAttribute("data-sp-added");
  addedIds = [];
}
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== "add") {
    clearAdded();
    return;
  }
  // Shopify's line keys start with the variant id ("50123456789:9f8e…").
  const ids = (event.lines || [])
    .map((line) =>
      String(line.merchandiseId || "")
        .split("/")
        .pop(),
    )
    .filter((id) => /^\d+$/.test(id));
  event.promise
    ?.then(({ detail }) => {
      const drawer = bagDrawer();
      if (!drawer || detail?.didError) return;
      clearAdded();
      drawer.setAttribute("data-sp-added", "");
      addedIds = ids;
      // On the product page cart-drawer.js may already have opened the drawer (its handler on this add runs first):
      // the mark still lands before the drawer's first frame. After a quick add it opens later, when the sheet closes.
      if (drawer.hasAttribute("open")) announceAdded(drawer);
      else addedTimer = setTimeout(clearAdded, 6000);
    })
    .catch(() => {});
});
// Screen readers hear what was added; the visible line says "Added to your bag". The live region sits on the dialog
// itself, outside the markup Horizon redraws after an add (which would empty it), and the name is read once the new
// line has been drawn in (up to a second; otherwise just "Added to your bag.").
function bagLiveRegion(drawer) {
  const dialog = drawer.querySelector("dialog");
  let live = dialog?.querySelector(":scope > [data-sp-bag-live]");
  if (dialog && !live) {
    live = document.createElement("p");
    live.className = "sp-visually-hidden";
    live.setAttribute("role", "status");
    live.dataset.spBagLive = "";
    dialog.append(live);
  }
  return live;
}
function announceAdded(drawer, tries = 0) {
  clearTimeout(addedTimer);
  if (!drawer.hasAttribute("data-sp-added")) return;
  const row = [...drawer.querySelectorAll(".cart-items__table-row[data-key]")].find((line) =>
    addedIds.includes(line.dataset.key.split(":")[0]),
  );
  const name = row?.querySelector(".cart-items__title")?.textContent.trim();
  if (!name && tries < 5) {
    addedTimer = setTimeout(() => announceAdded(drawer, tries + 1), 200);
    return;
  }
  const live = bagLiveRegion(drawer);
  if (live) live.textContent = name ? `${name} added to your bag.` : "Added to your bag.";
}
if (bagDrawer()) bagLiveRegion(bagDrawer());
document.addEventListener("theme-drawer:open", (event) => {
  const drawer = event.target;
  if (drawer instanceof Element && drawer.id === "cart-drawer" && drawer.hasAttribute("data-sp-added"))
    announceAdded(drawer);
});
// The dialog's own close event comes after the closing slide, so the drawer never changes while it is still visible.
bagDrawer()
  ?.querySelector("dialog")
  ?.addEventListener("close", () => {
    clearAdded();
    const live = bagDrawer()?.querySelector("[data-sp-bag-live]");
    if (live) live.textContent = "";
  });

/* Round 5: the line under Add to bag ("Free delivery from ₹999 · 15-day returns", blocks/sp-product-assurance.liquid)
   links to the page's "Delivery and returns" (id sp-delivery in sp-product-story). Opening it here means the shopper
   lands on the answer, not on a closed heading; focus goes to its summary so keyboard and screen-reader users land
   there too. An address ending in #sp-delivery opens it the same way. */
function openDelivery(scroll) {
  const details = document.getElementById("sp-delivery");
  if (!(details instanceof HTMLDetailsElement)) return false;
  details.open = true;
  const summary = details.querySelector("summary");
  if (scroll) details.scrollIntoView({ block: "start", behavior: scrollBehavior() });
  summary?.focus({ preventScroll: true });
  return true;
}
document.addEventListener("click", (event) => {
  const link = event.target instanceof Element ? event.target.closest("[data-sp-assure]") : null;
  if (!link || !openDelivery(true)) return;
  event.preventDefault();
});
if (window.location.hash === "#sp-delivery") openDelivery(false);

/* Round 5: focus never falls out of the open bag drawer. Horizon redraws the drawer's insides (.cart-drawer__inner) a
   moment after an add, and again after a quantity change or removal; when the control that had focus is replaced,
   focus drops to the page body and the next Tab leaves the drawer (seen in the Round 5 keyboard check: 2 of 31 Tab
   stops escaped right after Add to bag). After each redraw, if the drawer is open and focus is no longer inside it,
   it goes back to the same control when there is one (same line, same role), else to the close button. */
{
  const dialog = bagDrawer()?.querySelector("dialog");
  if (dialog) {
    let lastKey = null;
    const keyOf = (el) => {
      const row = el.closest("[data-key]");
      const role = [
        "quantity-plus",
        "quantity-minus",
        "cart-items__remove",
        "cart-items__title",
        "cart-discount__summary",
        "cart__checkout-button",
        "sp-bag-continue",
      ].find((c) => el.classList.contains(c));
      return role ? { row: row?.dataset.key || "", role } : null;
    };
    dialog.addEventListener("focusin", (event) => {
      if (event.target instanceof Element) lastKey = keyOf(event.target);
    });
    new MutationObserver(() => {
      if (!dialog.open || dialog.contains(document.activeElement)) return;
      let target = null;
      if (lastKey)
        target = dialog.querySelector(
          `${lastKey.row ? `[data-key="${CSS.escape(lastKey.row)}"] ` : ""}.${lastKey.role}`,
        );
      if (!target || target.disabled) target = dialog.querySelector(".theme-drawer__close-button");
      target?.focus({ preventScroll: true });
    }).observe(dialog, { childList: true, subtree: true });
    // Horizon's trap (theme-drawer.js -> focus.js trapFocus) remembers the first and last controls when the drawer
    // opens; the redraw after an add replaces them, so Tab from the last control left for the page and Shift+Tab from
    // the first went nowhere (Round 4A as well, only after an add). This wraps with the controls as they are now.
    const focusables = () =>
      [
        ...dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select, textarea, summary, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter(
        (el) =>
          el.getClientRects().length &&
          getComputedStyle(el).visibility !== "hidden" &&
          !el.closest("[hidden], [inert]"),
      );
    dialog.addEventListener("keydown", (event) => {
      if (event.key !== "Tab" || !dialog.open || event.defaultPrevented) return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      }
    });
  }
}

/* Round 6: "Copy link" in a blog post's share line (sections/sp-article.liquid): the page's address goes to the
   clipboard and "Link copied" is announced (and shown on the button for 2 s). Where the clipboard is not allowed, the
   browser's own share sheet is offered instead, if it has one. */
document.addEventListener("click", async (event) => {
  const button = event.target instanceof Element ? event.target.closest("[data-sp-copy]") : null;
  if (!button) return;
  const url = button.dataset.spCopy;
  const status = button.closest(".sp-article-share")?.querySelector("[data-sp-copy-status]");
  try {
    await navigator.clipboard.writeText(url);
    const label = button.textContent;
    button.textContent = "Link copied";
    if (status) status.textContent = "Link copied";
    setTimeout(() => {
      button.textContent = label;
      if (status) status.textContent = "";
    }, 2000);
  } catch {
    if (navigator.share) navigator.share({ url }).catch(() => {});
  }
});
